import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, ChevronDown, X } from 'lucide-react';
import { getCategoryEmoji, getCategoryDisplayName, getSubCategoryDisplayName } from '../categoryUtils';

export type CategoryFilter = 'all' | 'food' | 'non-food';

export const formatCategoryName = (category: string, mainCategory?: string) => {
  const displayName = getCategoryDisplayName(category, mainCategory) || category;
  const emoji = getCategoryEmoji(category);
  return `${emoji} ${displayName}`;
};

export const formatSubCategoryName = (subCategory: string) => {
  const displayName = getSubCategoryDisplayName(subCategory) || subCategory;
  return displayName;
};

interface FilterControlsProps {
  categoryFilter: CategoryFilter;
  setCategoryFilter: (filter: CategoryFilter) => void;
  platformFilters: string[];
  setPlatformFilters: (platforms: string[]) => void;
  availablePlatforms: string[];
  categoryFilters: string[];
  setCategoryFilters: (categories: string[]) => void;
  availableCategories: string[];
  subCategoryFilters?: string[];
  setSubCategoryFilters?: (subCategories: string[]) => void;
  availableSubCategories?: string[];
  restaurantFilters?: string[];
  setRestaurantFilters?: (restaurants: string[]) => void;
  availableRestaurants?: { id: string; name: string }[];
  showRestaurantFilter?: boolean;
  dateRange?: { start: Date | null; end: Date | null };
  setDateRange?: (range: { start: Date | null; end: Date | null }) => void;
  showDateRangeFilter?: boolean;
  advancedFilters?: {
    minAvgWeeklySpent?: number;
    maxAvgWeeklySpent?: number;
    minOrderFrequency?: number;
    maxOrderFrequency?: number;
    minAvgOrderQuantity?: number;
    maxAvgOrderQuantity?: number;
  };
  setAdvancedFilters?: (filters: {
    minAvgWeeklySpent?: number;
    maxAvgWeeklySpent?: number;
    minOrderFrequency?: number;
    maxOrderFrequency?: number;
    minAvgOrderQuantity?: number;
    maxAvgOrderQuantity?: number;
  }) => void;
  showAdvancedFilters?: boolean;
}

export const FilterControls: React.FC<FilterControlsProps> = ({
  categoryFilter,
  setCategoryFilter,
  platformFilters,
  setPlatformFilters,
  availablePlatforms,
  categoryFilters,
  setCategoryFilters,
  availableCategories,
  subCategoryFilters = [],
  setSubCategoryFilters,
  availableSubCategories = [],
  restaurantFilters = [],
  setRestaurantFilters,
  availableRestaurants = [],
  showRestaurantFilter = false,
  dateRange,
  setDateRange,
  showDateRangeFilter = false,
  advancedFilters,
  setAdvancedFilters,
  showAdvancedFilters = false
}) => {
  const { t } = useTranslation();
  const [isPlatformDropdownOpen, setIsPlatformDropdownOpen] = useState(false);
  const [isFoodFilterDropdownOpen, setIsFoodFilterDropdownOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isSubCategoryDropdownOpen, setIsSubCategoryDropdownOpen] = useState(false);
  const [isRestaurantDropdownOpen, setIsRestaurantDropdownOpen] = useState(false);
  const [isDateRangeDropdownOpen, setIsDateRangeDropdownOpen] = useState(false);
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
  const platformDropdownRef = useRef<HTMLDivElement>(null);
  const foodFilterDropdownRef = useRef<HTMLDivElement>(null);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const subCategoryDropdownRef = useRef<HTMLDivElement>(null);
  const restaurantDropdownRef = useRef<HTMLDivElement>(null);
  const dateRangeDropdownRef = useRef<HTMLDivElement>(null);
  const advancedFiltersRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (platformDropdownRef.current && !platformDropdownRef.current.contains(event.target as Node)) {
        setIsPlatformDropdownOpen(false);
      }
      if (foodFilterDropdownRef.current && !foodFilterDropdownRef.current.contains(event.target as Node)) {
        setIsFoodFilterDropdownOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false);
      }
      if (subCategoryDropdownRef.current && !subCategoryDropdownRef.current.contains(event.target as Node)) {
        setIsSubCategoryDropdownOpen(false);
      }
      if (restaurantDropdownRef.current && !restaurantDropdownRef.current.contains(event.target as Node)) {
        setIsRestaurantDropdownOpen(false);
      }
      if (dateRangeDropdownRef.current && !dateRangeDropdownRef.current.contains(event.target as Node)) {
        setIsDateRangeDropdownOpen(false);
      }
      if (advancedFiltersRef.current && !advancedFiltersRef.current.contains(event.target as Node)) {
        setIsAdvancedFiltersOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePlatformToggle = (platform: string) => {
    if (platformFilters.includes(platform)) {
      setPlatformFilters(platformFilters.filter(p => p !== platform));
    } else {
      setPlatformFilters([...platformFilters, platform]);
    }
  };

  const clearPlatformFilters = () => {
    setPlatformFilters([]);
  };

  const handleCategoryToggle = (category: string) => {
    if (categoryFilters.includes(category)) {
      setCategoryFilters(categoryFilters.filter(c => c !== category));
    } else {
      setCategoryFilters([...categoryFilters, category]);
    }
  };

  const clearCategoryFilters = () => {
    setCategoryFilters([]);
  };

  const handleSubCategoryToggle = (subCategory: string) => {
    if (!setSubCategoryFilters) return;
    if (subCategoryFilters.includes(subCategory)) {
      setSubCategoryFilters(subCategoryFilters.filter(sc => sc !== subCategory));
    } else {
      setSubCategoryFilters([...subCategoryFilters, subCategory]);
    }
  };

  const clearSubCategoryFilters = () => {
    if (setSubCategoryFilters) {
      setSubCategoryFilters([]);
    }
  };

  const handleRestaurantToggle = (restaurantId: string) => {
    if (!setRestaurantFilters) return;
    if (restaurantFilters.includes(restaurantId)) {
      setRestaurantFilters(restaurantFilters.filter(r => r !== restaurantId));
    } else {
      setRestaurantFilters([...restaurantFilters, restaurantId]);
    }
  };

  const clearRestaurantFilters = () => {
    if (setRestaurantFilters) {
      setRestaurantFilters([]);
    }
  };

  const formatPlatformName = (platform: string) => {
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  const formatFoodFilterName = (filter: CategoryFilter) => {
    const displayMap: Record<CategoryFilter, string> = {
      'all': t('filter.allItems') || 'All Items',
      'food': t('filter.foodOnly') || 'Food Only',
      'non-food': t('filter.nonFoodOnly') || 'Non-Food Only'
    };
    return displayMap[filter];
  };


  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getDateRangePresets = () => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return [
      {
        label: 'Last 7 days',
        range: { 
          start: new Date(startOfToday.getTime() - 6 * 24 * 60 * 60 * 1000), 
          end: startOfToday 
        }
      },
      {
        label: 'Last 30 days',
        range: { 
          start: new Date(startOfToday.getTime() - 29 * 24 * 60 * 60 * 1000), 
          end: startOfToday 
        }
      },
      {
        label: 'Last 3 months',
        range: { 
          start: new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()), 
          end: startOfToday 
        }
      },
      {
        label: 'Last 6 months',
        range: { 
          start: new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()), 
          end: startOfToday 
        }
      },
      {
        label: 'Last year',
        range: { 
          start: new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()), 
          end: startOfToday 
        }
      }
    ];
  };

  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-3">
      <Filter className="w-4 h-4 text-muted dark:text-muted-dark" />

{/* Restaurant Filter */}
{showRestaurantFilter && availableRestaurants.length > 0 && setRestaurantFilters && (
        <div className="relative" ref={restaurantDropdownRef}>
          <button
            onClick={() => setIsRestaurantDropdownOpen(!isRestaurantDropdownOpen)}
            className="flex items-center gap-2 px-3 py-2 bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-lg text-sm font-medium hover:bg-muted/10 dark:hover:bg-muted-dark/10 transition-colors"
          >
            <span className="text-text dark:text-text-dark">
              {restaurantFilters.length === 0 
                ? 'All Restaurants'
                : restaurantFilters.length === 1 
                  ? availableRestaurants.find(r => r.id === restaurantFilters[0])?.name || restaurantFilters[0]
                  : `${restaurantFilters.length} restaurants`
              }
            </span>
            <ChevronDown className="w-4 h-4 text-muted dark:text-muted-dark" />
          </button>

          {isRestaurantDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 w-60 bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-lg shadow-lg z-50">
              <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted dark:text-muted-dark">Restaurants</span>
                  {restaurantFilters.length === 0 ? (
                    <button
                      onClick={() => setRestaurantFilters(availableRestaurants.map(r => r.id))}
                      className="text-xs text-primary dark:text-primary-dark hover:underline"
                    >
                      {t('filter.selectAll') || 'Select All'}
                    </button>
                  ) : (
                    <button
                      onClick={clearRestaurantFilters}
                      className="text-xs text-primary dark:text-primary-dark hover:underline"
                    >
                      {t('filter.clearAll') || 'Clear All'}
                    </button>
                  )}
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {availableRestaurants.map(restaurant => (
                    <label
                      key={restaurant.id}
                      className="flex items-center gap-2 p-1 rounded hover:bg-muted/10 dark:hover:bg-muted-dark/10 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={restaurantFilters.includes(restaurant.id)}
                        onChange={() => handleRestaurantToggle(restaurant.id)}
                        className="rounded border-border dark:border-border-dark"
                      />
                      <span className="text-sm text-text dark:text-text-dark">
                        {restaurant.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Food/Non-Food Filter */}
      <div className="relative" ref={foodFilterDropdownRef}>
        <button
          onClick={() => setIsFoodFilterDropdownOpen(!isFoodFilterDropdownOpen)}
          className="flex items-center gap-2 px-3 py-2 bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-lg text-sm font-medium hover:bg-muted/10 dark:hover:bg-muted-dark/10 transition-colors"
        >
          <span className="text-text dark:text-text-dark">
            {formatFoodFilterName(categoryFilter)}
          </span>
          <ChevronDown className="w-4 h-4 text-muted dark:text-muted-dark" />
        </button>

        {isFoodFilterDropdownOpen && (
          <div className="absolute top-full left-0 mt-1 w-48 bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-lg shadow-lg z-50">
            <div className="p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted dark:text-muted-dark">Food Type</span>
              </div>
              <div className="space-y-1">
                {(['all', 'food', 'non-food'] as CategoryFilter[]).map(filter => (
                  <button
                    key={filter}
                    onClick={() => {
                      setCategoryFilter(filter);
                      setIsFoodFilterDropdownOpen(false);
                    }}
                    className={`w-full text-left px-2 py-1 rounded hover:bg-muted/10 dark:hover:bg-muted-dark/10 cursor-pointer transition-colors ${
                      categoryFilter === filter 
                        ? 'bg-primary/10 dark:bg-primary-dark/10 text-primary dark:text-primary-dark font-medium' 
                        : 'text-text dark:text-text-dark'
                    }`}
                  >
                    <span className="text-sm">
                      {formatFoodFilterName(filter)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Category Filter Dropdown */}
      <div className="relative" ref={categoryDropdownRef}>
        <button
          onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
          className="flex items-center gap-2 px-3 py-2 bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-lg text-sm font-medium hover:bg-muted/10 dark:hover:bg-muted-dark/10 transition-colors"
        >
          <span className="text-text dark:text-text-dark">
            {categoryFilters.length === 0 
              ? t('filter.allCategories') || 'All Categories'
              : categoryFilters.length === 1 
                ? formatCategoryName(categoryFilters[0])
                : `${categoryFilters.length} categories`
            }
          </span>
          <ChevronDown className="w-4 h-4 text-muted dark:text-muted-dark" />
        </button>

        {isCategoryDropdownOpen && (
          <div className="absolute top-full left-0 mt-1 w-60 bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-lg shadow-lg z-50">
            <div className="p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted dark:text-muted-dark">Categories</span>
                {categoryFilters.length === 0 ? (
                  <button
                    onClick={() => setCategoryFilters([...availableCategories])}
                    className="text-xs text-primary dark:text-primary-dark hover:underline"
                  >
                    {t('filter.selectAll') || 'Select All'}
                  </button>
                ) : (
                  <button
                    onClick={clearCategoryFilters}
                    className="text-xs text-primary dark:text-primary-dark hover:underline"
                  >
                    {t('filter.clearAll')}
                  </button>
                )}
              </div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {availableCategories.map(category => (
                  <label
                    key={category}
                    className="flex items-center gap-2 p-1 rounded hover:bg-muted/10 dark:hover:bg-muted-dark/10 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={categoryFilters.includes(category)}
                      onChange={() => handleCategoryToggle(category)}
                      className="rounded border-border dark:border-border-dark"
                    />
                    <span className="text-sm text-text dark:text-text-dark">
                      {formatCategoryName(category)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Subcategory Filter Dropdown */}
      {setSubCategoryFilters && availableSubCategories.length > 0 && (
        <div className="relative" ref={subCategoryDropdownRef}>
          <button
            onClick={() => setIsSubCategoryDropdownOpen(!isSubCategoryDropdownOpen)}
            className="flex items-center gap-2 px-3 py-2 bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-lg text-sm font-medium hover:bg-muted/10 dark:hover:bg-muted-dark/10 transition-colors"
          >
            <span className="text-text dark:text-text-dark">
              {subCategoryFilters.length === 0 
                ? 'All Subcategories'
                : subCategoryFilters.length === 1 
                  ? formatSubCategoryName(subCategoryFilters[0])
                  : `${subCategoryFilters.length} subcategories`
              }
            </span>
            <ChevronDown className="w-4 h-4 text-muted dark:text-muted-dark" />
          </button>

          {isSubCategoryDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 w-60 bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-lg shadow-lg z-50">
              <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted dark:text-muted-dark">Subcategories</span>
                  {subCategoryFilters.length === 0 ? (
                    <button
                      onClick={() => setSubCategoryFilters([...availableSubCategories])}
                      className="text-xs text-primary dark:text-primary-dark hover:underline"
                    >
                      {t('filter.selectAll') || 'Select All'}
                    </button>
                  ) : (
                    <button
                      onClick={clearSubCategoryFilters}
                      className="text-xs text-primary dark:text-primary-dark hover:underline"
                    >
                      {t('filter.clearAll')}
                    </button>
                  )}
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {availableSubCategories.map(subCategory => (
                    <label
                      key={subCategory}
                      className="flex items-center gap-2 p-1 rounded hover:bg-muted/10 dark:hover:bg-muted-dark/10 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={subCategoryFilters.includes(subCategory)}
                        onChange={() => handleSubCategoryToggle(subCategory)}
                        className="rounded border-border dark:border-border-dark"
                      />
                      <span className="text-sm text-text dark:text-text-dark">
                        {formatSubCategoryName(subCategory)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Platform Filter */}
      <div className="relative" ref={platformDropdownRef}>
        <button
          onClick={() => setIsPlatformDropdownOpen(!isPlatformDropdownOpen)}
          className="flex items-center gap-2 px-3 py-2 bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-lg text-sm font-medium hover:bg-muted/10 dark:hover:bg-muted-dark/10 transition-colors"
        >
          <span className="text-text dark:text-text-dark">
            {platformFilters.length === 0 
              ? t('filter.allSuppliers') 
              : platformFilters.length === 1 
                ? formatPlatformName(platformFilters[0])
                : `${platformFilters.length} ${t('filter.suppliers')}`
            }
          </span>
          <ChevronDown className="w-4 h-4 text-muted dark:text-muted-dark" />
        </button>

        {isPlatformDropdownOpen && (
          <div className="absolute top-full left-0 mt-1 w-48 bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-lg shadow-lg z-50">
            <div className="p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted dark:text-muted-dark">{t('filter.suppliers')}</span>
                {platformFilters.length === 0 ? (
                  <button
                    onClick={() => setPlatformFilters([...availablePlatforms])}
                    className="text-xs text-primary dark:text-primary-dark hover:underline"
                  >
                    {t('filter.selectAll') || 'Select All'}
                  </button>
                ) : (
                  <button
                    onClick={clearPlatformFilters}
                    className="text-xs text-primary dark:text-primary-dark hover:underline"
                  >
                    {t('filter.clearAll')}
                  </button>
                )}
              </div>
              <div className="space-y-1">
                {availablePlatforms.map(platform => (
                  <label
                    key={platform}
                    className="flex items-center gap-2 p-1 rounded hover:bg-muted/10 dark:hover:bg-muted-dark/10 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={platformFilters.includes(platform)}
                      onChange={() => handlePlatformToggle(platform)}
                      className="rounded border-border dark:border-border-dark"
                    />
                    <span className="text-sm text-text dark:text-text-dark">
                      {formatPlatformName(platform)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Date Range Filter */}
      {showDateRangeFilter && (
        <div className="relative" ref={dateRangeDropdownRef}>
          <button
            onClick={() => setIsDateRangeDropdownOpen(!isDateRangeDropdownOpen)}
            className="flex items-center gap-2 px-3 py-2 bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-lg text-sm font-medium hover:bg-muted/10 dark:hover:bg-muted-dark/10 transition-colors"
          >
            <span className="text-text dark:text-text-dark">
              {!dateRange?.start && !dateRange?.end 
                ? 'All Time'
                : dateRange?.start && dateRange?.end
                  ? `${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}`
                  : dateRange?.start 
                    ? `From ${formatDate(dateRange.start)}`
                    : `Until ${formatDate(dateRange.end!)}`
              }
            </span>
            <ChevronDown className="w-4 h-4 text-muted dark:text-muted-dark" />
          </button>

          {isDateRangeDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-lg shadow-lg z-50">
              <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted dark:text-muted-dark">Date Range</span>
                  {(dateRange?.start || dateRange?.end) && (
                    <button
                      onClick={() => setDateRange?.({ start: null, end: null })}
                      className="text-xs text-primary dark:text-primary-dark hover:underline"
                    >
                      Clear
                    </button>
                  )}
                </div>
                
                {/* Quick Presets */}
                <div className="space-y-1 mb-3">
                  {getDateRangePresets().map((preset, index) => (
                    <button
                      key={index}
                      onClick={() => setDateRange?.(preset.range)}
                      className="w-full text-left px-2 py-1 text-sm text-text dark:text-text-dark hover:bg-muted/10 dark:hover:bg-muted-dark/10 rounded transition-colors"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                
                {/* Custom Date Range */}
                <div className="border-t border-border dark:border-border-dark pt-3">
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs text-muted dark:text-muted-dark mb-1">From</label>
                      <input
                        type="date"
                        value={dateRange?.start ? dateRange.start.toISOString().split('T')[0] : ''}
                        onChange={(e) => setDateRange?.({ 
                          start: e.target.value ? new Date(e.target.value) : null, 
                          end: dateRange?.end || null 
                        })}
                        className="w-full px-2 py-1 text-xs border border-border dark:border-border-dark rounded bg-background dark:bg-background-dark text-text dark:text-text-dark"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted dark:text-muted-dark mb-1">To</label>
                      <input
                        type="date"
                        value={dateRange?.end ? dateRange.end.toISOString().split('T')[0] : ''}
                        onChange={(e) => setDateRange?.({ 
                          start: dateRange?.start || null, 
                          end: e.target.value ? new Date(e.target.value) : null 
                        })}
                        className="w-full px-2 py-1 text-xs border border-border dark:border-border-dark rounded bg-background dark:bg-background-dark text-text dark:text-text-dark"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Advanced Filters */}
      {showAdvancedFilters && (
        <div className="relative" ref={advancedFiltersRef}>
          <button
            onClick={() => setIsAdvancedFiltersOpen(!isAdvancedFiltersOpen)}
            className="flex items-center gap-2 px-3 py-2 bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-lg text-sm font-medium hover:bg-muted/10 dark:hover:bg-muted-dark/10 transition-colors"
          >
            <span className="text-text dark:text-text-dark">Advanced Filters</span>
            <ChevronDown className="w-4 h-4 text-muted dark:text-muted-dark" />
          </button>

          {isAdvancedFiltersOpen && (
            <div className="absolute top-full left-0 mt-1 w-80 bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-lg shadow-lg z-50">
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-text dark:text-text-dark">Advanced Filters</span>
                  {(advancedFilters && Object.values(advancedFilters).some(v => v !== undefined)) && (
                    <button
                      onClick={() => setAdvancedFilters?.({})}
                      className="text-xs text-primary dark:text-primary-dark hover:underline"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                
                <div className="space-y-4">
                  {/* Average Weekly Spent */}
                  <div>
                    <label className="block text-xs font-medium text-muted dark:text-muted-dark mb-2">
                      Average Weekly Spent (€)
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={advancedFilters?.minAvgWeeklySpent || ''}
                        onChange={(e) => setAdvancedFilters?.({
                          ...advancedFilters,
                          minAvgWeeklySpent: e.target.value ? Number(e.target.value) : undefined
                        })}
                        className="w-20 px-2 py-1 text-xs border border-border dark:border-border-dark rounded bg-background dark:bg-background-dark text-text dark:text-text-dark"
                      />
                      <span className="text-xs text-muted dark:text-muted-dark">to</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={advancedFilters?.maxAvgWeeklySpent || ''}
                        onChange={(e) => setAdvancedFilters?.({
                          ...advancedFilters,
                          maxAvgWeeklySpent: e.target.value ? Number(e.target.value) : undefined
                        })}
                        className="w-20 px-2 py-1 text-xs border border-border dark:border-border-dark rounded bg-background dark:bg-background-dark text-text dark:text-text-dark"
                      />
                    </div>
                  </div>

                  {/* Order Frequency */}
                  <div>
                    <label className="block text-xs font-medium text-muted dark:text-muted-dark mb-2">
                      Order Frequency
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={advancedFilters?.minOrderFrequency || ''}
                        onChange={(e) => setAdvancedFilters?.({
                          ...advancedFilters,
                          minOrderFrequency: e.target.value ? Number(e.target.value) : undefined
                        })}
                        className="w-20 px-2 py-1 text-xs border border-border dark:border-border-dark rounded bg-background dark:bg-background-dark text-text dark:text-text-dark"
                      />
                      <span className="text-xs text-muted dark:text-muted-dark">to</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={advancedFilters?.maxOrderFrequency || ''}
                        onChange={(e) => setAdvancedFilters?.({
                          ...advancedFilters,
                          maxOrderFrequency: e.target.value ? Number(e.target.value) : undefined
                        })}
                        className="w-20 px-2 py-1 text-xs border border-border dark:border-border-dark rounded bg-background dark:bg-background-dark text-text dark:text-text-dark"
                      />
                    </div>
                  </div>

                  {/* Average Order Quantity */}
                  <div>
                    <label className="block text-xs font-medium text-muted dark:text-muted-dark mb-2">
                      Average Order Quantity
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        step="0.1"
                        placeholder="Min"
                        value={advancedFilters?.minAvgOrderQuantity || ''}
                        onChange={(e) => setAdvancedFilters?.({
                          ...advancedFilters,
                          minAvgOrderQuantity: e.target.value ? Number(e.target.value) : undefined
                        })}
                        className="w-20 px-2 py-1 text-xs border border-border dark:border-border-dark rounded bg-background dark:bg-background-dark text-text dark:text-text-dark"
                      />
                      <span className="text-xs text-muted dark:text-muted-dark">to</span>
                      <input
                        type="number"
                        step="0.1"
                        placeholder="Max"
                        value={advancedFilters?.maxAvgOrderQuantity || ''}
                        onChange={(e) => setAdvancedFilters?.({
                          ...advancedFilters,
                          maxAvgOrderQuantity: e.target.value ? Number(e.target.value) : undefined
                        })}
                        className="w-20 px-2 py-1 text-xs border border-border dark:border-border-dark rounded bg-background dark:bg-background-dark text-text dark:text-text-dark"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Active Filters Display */}
      {(categoryFilter !== 'all' || categoryFilters.length > 0 || subCategoryFilters.length > 0 || platformFilters.length > 0 || restaurantFilters.length > 0 || (showDateRangeFilter && (dateRange?.start || dateRange?.end)) || (showAdvancedFilters && advancedFilters && Object.values(advancedFilters).some(v => v !== undefined))) && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted dark:text-muted-dark">{t('filter.filters')}</span>

          {restaurantFilters.map(restaurantId => {
            const restaurant = availableRestaurants.find(r => r.id === restaurantId);
            return (
              <div key={restaurantId} className="flex items-center gap-1 px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 rounded-full text-xs">
                <span>{restaurant?.name || restaurantId}</span>
                <button
                  onClick={() => handleRestaurantToggle(restaurantId)}
                  className="hover:bg-indigo-200 dark:hover:bg-indigo-800/50 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
          
          {categoryFilter !== 'all' && (
            <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full text-xs">
              <span>{formatFoodFilterName(categoryFilter)}</span>
              <button
                onClick={() => setCategoryFilter('all')}
                className="hover:bg-blue-200 dark:hover:bg-blue-800/50 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          
          {categoryFilters.map(category => (
            <div key={category} className="flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 rounded-full text-xs">
              <span>{formatCategoryName(category)}</span>
              <button
                onClick={() => handleCategoryToggle(category)}
                className="hover:bg-purple-200 dark:hover:bg-purple-800/50 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          
          {subCategoryFilters.map(subCategory => (
            <div key={subCategory} className="flex items-center gap-1 px-2 py-1 bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-200 rounded-full text-xs">
              <span>{formatSubCategoryName(subCategory)}</span>
              <button
                onClick={() => handleSubCategoryToggle(subCategory)}
                className="hover:bg-pink-200 dark:hover:bg-pink-800/50 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          
          {platformFilters.map(platform => (
            <div key={platform} className="flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded-full text-xs">
              <span>{formatPlatformName(platform)}</span>
              <button
                onClick={() => handlePlatformToggle(platform)}
                className="hover:bg-green-200 dark:hover:bg-green-800/50 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          
          {showDateRangeFilter && (dateRange?.start || dateRange?.end) && (
            <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 rounded-full text-xs">
              <span>
                {dateRange?.start && dateRange?.end
                  ? `${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}`
                  : dateRange?.start 
                    ? `From ${formatDate(dateRange.start)}`
                    : `Until ${formatDate(dateRange.end!)}`
                }
              </span>
              <button
                onClick={() => setDateRange?.({ start: null, end: null })}
                className="hover:bg-orange-200 dark:hover:bg-orange-800/50 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          
          {showAdvancedFilters && advancedFilters && Object.entries(advancedFilters).filter(([_, value]) => value !== undefined).map(([key, value]) => {
            const getLabel = (key: string, value: number) => {
              switch(key) {
                case 'minAvgWeeklySpent': return `Min Weekly: €${value}`;
                case 'maxAvgWeeklySpent': return `Max Weekly: €${value}`;
                case 'minOrderFrequency': return `Min Orders: ${value}`;
                case 'maxOrderFrequency': return `Max Orders: ${value}`;
                case 'minAvgOrderQuantity': return `Min Qty: ${value}`;
                case 'maxAvgOrderQuantity': return `Max Qty: ${value}`;
                default: return `${key}: ${value}`;
              }
            };
            
            return (
              <div key={key} className="flex items-center gap-1 px-2 py-1 bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 rounded-full text-xs">
                <span>{getLabel(key, value!)}</span>
                <button
                  onClick={() => setAdvancedFilters?.({ ...advancedFilters, [key]: undefined })}
                  className="hover:bg-teal-200 dark:hover:bg-teal-800/50 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FilterControls;